import { useEffect, useState } from "react"

import type { TttChrome } from "@/components/ttt/types"
import Cart from "@/components/ttt/Cart"

interface NavbarProps {
  chrome: TttChrome
  isHome?: boolean
}

// Faithful React port of ttt/common/_navbar. Nav links are plain anchors so
// they work across paradigms (store/authors stay Slim during the migration).
export default function Navbar({ chrome, isHome = false }: NavbarProps) {
  const { paths, images, cart, csrf_token } = chrome
  const [mobileOpen, setMobileOpen] = useState(false)
  // On home the small navbar logo (#logo-navbar) is hidden until the visitor
  // scrolls past the hero (ttt--scroll-down); elsewhere (#home-logo-navbar) it
  // is always visible.
  const [logoVisible, setLogoVisible] = useState(false)

  useEffect(() => {
    if (!isHome) return
    const toggleVisibility = () =>
      setLogoVisible(window.scrollY > window.innerHeight)
    window.addEventListener("scroll", toggleVisibility)
    return () => window.removeEventListener("scroll", toggleVisibility)
  }, [isHome])

  // Faithful port of Ttt::Button::Component: a bordered button whose background
  // fills on hover while the label slides up, turns light and gains a trailing dot.
  const NavButton = ({ href, text }: { href: string; text: string }) => (
    <div className="relative h-full">
      <a
        href={href}
        className="relative peer pr-5 pl-1 h-full flex items-end overflow-hidden font-base text-primary border border-primary hover:border-content shadow-inner group"
      >
        <span className="absolute inset-0 w-full h-full opacity-0 duration-200 bg-content group-hover:opacity-100" />
        <span className="relative text-left uppercase transition-all duration-200 group-hover:pb-5 group-hover:text-light ease">
          {text} .
        </span>
      </a>
    </div>
  )

  return (
    <div className="absolute top-0 left-0 right-0 z-50">
      <div className="fixed top-10 right-6 z-50">
        <Cart cart={cart} csrfToken={csrf_token} backpackSrc={images.backpack} />
      </div>

      {/* Desktop */}
      <div className="w-full hidden md:flex cursor-default">
        <div className="flex px-24 items-center justify-end w-full transition py-8 z-40">
          <div
            id={isHome ? "logo-navbar" : "home-logo-navbar"}
            className={isHome && logoVisible ? "visible" : undefined}
          >
            <a href={paths.root}>
              <img src={images.logo} alt="TTT" className="w-48" />
            </a>
          </div>
          <div className="flex gap-6 h-12 items-center">
            <NavButton href={paths.authors} text="Auteur.ices" />
            <NavButton href={paths.store} text="Shop" />
            <NavButton href={paths.about} text="À propos" />
          </div>
        </div>
      </div>

      {/* Mobile */}
      <div className="md:hidden">
        <button
          type="button"
          aria-label="Menu"
          className="fixed shadow-xl z-40 top-4 left-4 rounded-full"
          onClick={() => setMobileOpen(true)}
        >
          <div className="w-16 h-16 bg-content flex items-center justify-center rounded-full p-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-6 h-6 text-light"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
              />
            </svg>
          </div>
        </button>

        {mobileOpen && (
          <div className="z-50 fixed top-0 left-0 right-0 bottom-0 bg-content/90">
            <button
              type="button"
              aria-label="Fermer"
              className="absolute top-10 right-10"
              onClick={() => setMobileOpen(false)}
            >
              <div className="w-16 h-16 flex items-center justify-center rounded-full p-3 text-light">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-12 h-12"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            </button>
            <div className="flex flex-col justify-around items-start py-12 px-12">
              <a href={paths.about} className="tracking-widest text-5xl text-light mb-8">
                À propos
              </a>
              <a href={paths.authors} className="tracking-widest text-5xl text-light mb-8">
                Auteur.ices
              </a>
              <a href={paths.store} className="tracking-widest text-5xl text-light mb-8">
                Shop
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
